/**
 * Reglas de negocio de pagos, distribucion por concepto y anulaciones.
 *
 * Este modulo es PURO: no toca base de datos, red ni reloj del sistema. Recibe el
 * estado relevante ya cargado y devuelve el resultado a persistir. La misma funcion
 * corre en la PWA (validacion previa, offline) y en la API (validacion autoritativa),
 * de modo que cliente y servidor no pueden divergir.
 */

import { type Instant, type OperationalDate } from "./clock.js";
import { assertDomain } from "./errors.js";
import { idempotencyKey, type Ulid } from "./ids.js";
import {
  applyToMonthlyCharge,
  type MonthlyChargeState,
  totalOutstanding,
} from "./lateFees.js";
import {
  add,
  compare,
  equals,
  formatMoney,
  isPositive,
  money,
  type Money,
  subtract,
  sum,
  zero,
} from "./money.js";

/**
 * Metodos de pago permitidos.
 *
 * Nunca se guarda un dato de tarjeta: para los metodos que piden referencia, esa
 * referencia es el numero de boleta o de autorizacion, y `validateReference`
 * rechaza cualquier cosa que se parezca a un numero de tarjeta.
 *
 * `CHEQUE` se llama «Tarjeta» y NO es un descuido. El colegio dejo de recibir
 * cheques y empezo a cobrar con tarjeta, y los cobros que ya se habian hecho con
 * ese metodo eran en realidad con tarjeta. Cambiar el codigo guardado obligaria a
 * reconstruir la tabla `payments` —SQLite no deja alterar un CHECK—, y esa tabla
 * la referencian cinco tablas con borrado en cascada: demasiado riesgo, sobre
 * dinero ya cobrado, para renombrar una etiqueta. El codigo es interno; lo que
 * ve el colegio es la etiqueta.
 */
export const PAYMENT_METHODS = [
  "EFECTIVO",
  "TRANSFERENCIA",
  "DEPOSITO",
  "CHEQUE",
  "SALDO_A_FAVOR",
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Readonly<Record<PaymentMethod, string>> =
  Object.freeze({
    EFECTIVO: "Efectivo",
    TRANSFERENCIA: "Transferencia",
    DEPOSITO: "Depósito",
    CHEQUE: "Tarjeta",
    SALDO_A_FAVOR: "Saldo a favor",
  });

/** Metodos que exigen una referencia (boleta, numero de cheque, etc.). */
const METHODS_REQUIRING_REFERENCE: ReadonlySet<PaymentMethod> =
  new Set<PaymentMethod>(["TRANSFERENCIA", "DEPOSITO", "CHEQUE"]);

/**
 * La referencia es un dato operativo (numero de boleta), NUNCA financiero sensible.
 * Se restringe a alfanumerico + guiones y se rechaza cualquier cosa que parezca
 * un numero de tarjeta (13-19 digitos seguidos).
 */
const REFERENCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9\-/]{2,39}$/;
const CARD_LIKE_PATTERN = /\d{13,}/;

export function normalizeReference(
  input: string | null | undefined,
): string | null {
  if (input === null || input === undefined) return null;
  const trimmed = String(input).trim().toUpperCase();
  return trimmed.length === 0 ? null : trimmed;
}

export function validateReference(
  method: PaymentMethod,
  reference: string | null,
): string | null {
  const normalized = normalizeReference(reference);
  if (METHODS_REQUIRING_REFERENCE.has(method)) {
    assertDomain(
      normalized !== null,
      "REFERENCIA_REQUERIDA",
      `El método ${PAYMENT_METHOD_LABELS[method]} requiere una referencia`,
      { method },
    );
  }
  if (normalized === null) return null;
  assertDomain(
    REFERENCE_PATTERN.test(normalized),
    "REFERENCIA_INVALIDA",
    "La referencia solo admite letras, números, guiones y barras (3 a 40 caracteres)",
    {},
  );
  assertDomain(
    !CARD_LIKE_PATTERN.test(normalized),
    "REFERENCIA_INVALIDA",
    "La referencia no puede contener secuencias largas de dígitos",
    {},
  );
  return normalized;
}

export const PAYMENT_STATUSES = [
  "PENDIENTE", // capturado localmente, aun no confirmado por el servidor
  "CONFIRMADO", // persistido y validado centralmente
  "RECHAZADO", // el servidor lo rechazo por una regla de negocio; no afecta saldos
  "EN_CONFLICTO", // requiere revision administrativa
  "ANULADO", // compensado por una operacion posterior
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_STATUS_LABELS: Readonly<Record<PaymentStatus, string>> =
  Object.freeze({
    PENDIENTE: "Pendiente de sincronización",
    CONFIRMADO: "Confirmado",
    RECHAZADO: "Rechazado",
    EN_CONFLICTO: "Requiere revisión",
    ANULADO: "Anulado",
  });

/**
 * Estados cuyo movimiento de dinero cuenta al reconstruir el saldo.
 *
 * ANULADO esta incluido a proposito: la anulacion no borra el movimiento original,
 * le suma una compensacion negativa. Si se excluyera el pago anulado quedaria solo la
 * compensacion y el saldo se descuadraria en el importe del pago.
 *
 * RECHAZADO y EN_CONFLICTO quedan fuera: ese dinero no se da por recibido hasta que
 * alguien resuelva el caso.
 */
export const EFFECTIVE_PAYMENT_STATUSES: ReadonlySet<PaymentStatus> =
  new Set<PaymentStatus>(["PENDIENTE", "CONFIRMADO", "ANULADO"]);

/** Los mismos estados, listos para un `IN (...)` de SQL. */
export const EFFECTIVE_PAYMENT_STATUS_SQL =
  "('PENDIENTE', 'CONFIRMADO', 'ANULADO')";

/** Cargo pendiente de un estudiante (obligacion de pago). */
export interface Charge {
  readonly id: Ulid;
  readonly studentId: Ulid;
  readonly cycleId: Ulid;
  readonly conceptId: Ulid;
  readonly conceptName: string;
  readonly conceptActive: boolean;
  /** Importe original del cargo. */
  readonly amount: Money;
  /** Importe ya aplicado por pagos vigentes. */
  readonly applied: Money;
  readonly dueDate: OperationalDate | null;
  /** Mes calendario 1-12 si es una mensualidad; `null` en conceptos sueltos. */
  readonly month?: number | null;
  /**
   * Estado de mora de la mensualidad. Cuando viene, el saldo del cargo deja de ser
   * solo capital: es mora + capital, y todo pago cubre primero la mora.
   * `null`/ausente en conceptos que no generan mora (inscripcion, material...).
   */
  readonly monthlyState?: MonthlyChargeState | null;
}

/**
 * Cuanto falta por cubrir de un cargo, MORA INCLUIDA.
 *
 * Para un concepto suelto es `monto - aplicado`. Para una mensualidad con mora es
 * `mora pendiente + capital pendiente`, calculado por el dominio de mora — nunca a
 * mano, para que pantalla, recibo y reporte no puedan discrepar.
 */
export function chargeOutstanding(
  charge: Charge,
  asOf?: OperationalDate,
): Money {
  if (charge.monthlyState != null && asOf !== undefined) {
    return totalOutstanding(charge.monthlyState, asOf);
  }
  const remaining = subtract(charge.amount, charge.applied);
  return remaining.minor < 0 ? zero(charge.amount.currency) : remaining;
}

/** Una linea de la distribucion del pago: cuanto se aplica a que cargo. */
export interface AllocationInput {
  readonly chargeId: Ulid;
  readonly amount: Money;
}

export interface PaymentAllocation {
  readonly chargeId: Ulid;
  readonly conceptId: Ulid;
  readonly conceptName: string;
  readonly amount: Money;
  /** Parte del importe que cubrio MORA. Cero en conceptos sin mora. */
  readonly lateFee: Money;
  /** Parte del importe que cubrio CAPITAL. */
  readonly capital: Money;
  /** Ciclo del cargo. Un mismo pago puede tener lineas de ciclos distintos. */
  readonly cycleId: Ulid;
  /** Mes de la mensualidad, o `null` si el concepto no maneja meses. */
  readonly month: number | null;
  /** `true` si esta linea deja el capital del cargo completamente saldado. */
  readonly settlesCapital: boolean;
  /** Saldo del cargo antes de aplicar este pago (mora + capital). */
  readonly balanceBefore: Money;
  /** Saldo del cargo despues de aplicar este pago. */
  readonly balanceAfter: Money;
}

export interface BuildPaymentInput {
  /** Identificador global generado en el dispositivo ANTES de guardar. */
  readonly paymentId: Ulid;
  readonly institutionId: Ulid;
  readonly cycleId: Ulid;
  readonly studentId: Ulid;
  readonly studentActive: boolean;
  readonly cycleClosed: boolean;
  readonly amount: Money;
  readonly method: PaymentMethod;
  readonly reference: string | null;
  readonly allocations: readonly AllocationInput[];
  readonly charges: readonly Charge[];
  /** Instante efectivo del pago (puede diferir de la captura si se registra en diferido). */
  readonly effectiveAt: Instant;
  readonly capturedAt: Instant;
  readonly operationalDate: OperationalDate;
  readonly userId: Ulid;
  readonly installationId: Ulid;
  readonly note: string | null;
  /** Si la institucion permite dejar excedente como saldo a favor. */
  readonly allowOverpayment: boolean;
  /** Grado/seccion vigentes al momento de la captura (se congelan en el recibo). */
  readonly gradeName: string;
  readonly sectionName: string;
}

export interface BuiltPayment {
  readonly paymentId: Ulid;
  readonly institutionId: Ulid;
  readonly cycleId: Ulid;
  readonly studentId: Ulid;
  readonly amount: Money;
  readonly method: PaymentMethod;
  readonly reference: string | null;
  readonly allocations: readonly PaymentAllocation[];
  /** Excedente que queda como saldo a favor del estudiante. */
  readonly creditAmount: Money;
  readonly effectiveAt: Instant;
  readonly capturedAt: Instant;
  readonly operationalDate: OperationalDate;
  readonly userId: Ulid;
  readonly installationId: Ulid;
  readonly note: string | null;
  readonly idempotencyKey: string;
  readonly gradeName: string;
  readonly sectionName: string;
  /** Suma de saldos pendientes del estudiante ANTES del pago. */
  readonly studentBalanceBefore: Money;
  /** Suma de saldos pendientes del estudiante DESPUES del pago. */
  readonly studentBalanceAfter: Money;
}

/**
 * Valida y arma un pago completo a partir de la captura del usuario.
 *
 * Reglas aplicadas (identicas en cliente y servidor):
 *  1. El importe total debe ser > 0 y de la moneda configurada.
 *  2. Debe existir al menos una linea de distribucion.
 *  3. Cada cargo referenciado debe existir, pertenecer al estudiante y estar activo.
 *  4. Ninguna linea puede exceder el saldo pendiente del cargo.
 *  5. La suma de las lineas no puede superar el importe total.
 *  6. La diferencia (excedente) solo se acepta si la institucion lo permite, y se
 *     registra explicitamente como saldo a favor — nunca se oculta.
 */
export function buildPayment(input: BuildPaymentInput): BuiltPayment {
  const currency = input.amount.currency;

  assertDomain(
    input.studentActive,
    "ESTUDIANTE_INACTIVO",
    "El estudiante no está activo",
    {
      studentId: input.studentId,
    },
  );
  assertDomain(
    !input.cycleClosed,
    "CICLO_CERRADO",
    "El ciclo escolar está cerrado",
    {
      cycleId: input.cycleId,
    },
  );
  assertDomain(
    isPositive(input.amount),
    "MONTO_NO_POSITIVO",
    "El importe debe ser mayor que cero",
    {
      amount: input.amount.minor,
    },
  );
  assertDomain(
    input.allocations.length > 0,
    "PAGO_SIN_DISTRIBUCION",
    "Debe indicar al menos un concepto al que aplicar el pago",
    {},
  );

  const reference = validateReference(input.method, input.reference);
  const chargesById = new Map(
    input.charges.map((charge) => [charge.id, charge]),
  );

  // Consolida lineas repetidas del mismo cargo antes de validar topes.
  const consolidated = new Map<Ulid, Money>();
  for (const line of input.allocations) {
    assertDomain(
      line.amount.currency === currency,
      "MONEDA_INCONSISTENTE",
      "Todas las líneas deben usar la misma moneda que el pago",
      { chargeId: line.chargeId },
    );
    assertDomain(
      isPositive(line.amount),
      "MONTO_NO_POSITIVO",
      "El importe aplicado a cada concepto debe ser mayor que cero",
      { chargeId: line.chargeId },
    );
    const previous = consolidated.get(line.chargeId) ?? zero(currency);
    consolidated.set(line.chargeId, add(previous, line.amount));
  }

  const allocations: PaymentAllocation[] = [];
  for (const [chargeId, amount] of consolidated) {
    const charge = chargesById.get(chargeId);
    assertDomain(
      charge !== undefined,
      "CARGO_NO_ENCONTRADO",
      "El concepto ya no está disponible",
      {
        chargeId,
      },
    );
    assertDomain(
      charge.studentId === input.studentId,
      "CARGO_DE_OTRO_ESTUDIANTE",
      "El concepto no corresponde al estudiante seleccionado",
      { chargeId },
    );
    assertDomain(
      charge.conceptActive,
      "CONCEPTO_INACTIVO",
      `El concepto "${charge.conceptName}" ya no está activo`,
      { chargeId, conceptId: charge.conceptId },
    );

    const balanceBefore = chargeOutstanding(charge, input.operationalDate);
    assertDomain(
      compare(amount, balanceBefore) <= 0,
      "CARGO_SOBREPAGADO",
      `El importe aplicado a "${charge.conceptName}" (${formatMoney(amount)}) supera su saldo pendiente (${formatMoney(balanceBefore)})`,
      { chargeId, applied: amount.minor, outstanding: balanceBefore.minor },
    );

    // Dentro de una mensualidad, el pago cubre PRIMERO la mora y luego el capital.
    // En un concepto sin mora todo es capital. El reparto lo decide el dominio de
    // mora, no esta funcion, para que exista una sola implementacion de la regla.
    const split =
      charge.monthlyState != null
        ? applyToMonthlyCharge(
            charge.monthlyState,
            amount,
            input.operationalDate,
          )
        : {
            toLateFee: zero(currency),
            toCapital: amount,
            remainder: zero(currency),
            settlesCapital:
              subtract(charge.amount, add(charge.applied, amount)).minor <= 0,
          };

    allocations.push({
      chargeId,
      conceptId: charge.conceptId,
      conceptName: charge.conceptName,
      amount,
      lateFee: split.toLateFee,
      capital: split.toCapital,
      cycleId: charge.cycleId,
      month: charge.month ?? null,
      settlesCapital: split.settlesCapital,
      balanceBefore,
      balanceAfter: subtract(balanceBefore, amount),
    });
  }

  const allocated = sum(
    allocations.map((allocation) => allocation.amount),
    currency,
  );
  assertDomain(
    compare(allocated, input.amount) <= 0,
    "DISTRIBUCION_NO_CUADRA",
    `La distribución (${formatMoney(allocated)}) supera el importe del pago (${formatMoney(input.amount)})`,
    { allocated: allocated.minor, total: input.amount.minor },
  );

  const creditAmount = subtract(input.amount, allocated);
  if (isPositive(creditAmount)) {
    assertDomain(
      input.allowOverpayment,
      "EXCEDENTE_NO_PERMITIDO",
      `Quedaría un excedente de ${formatMoney(creditAmount)} y la institución no permite saldo a favor`,
      { credit: creditAmount.minor },
    );
  }

  const studentBalanceBefore = sum(
    input.charges.map((charge) =>
      chargeOutstanding(charge, input.operationalDate),
    ),
    currency,
  );

  return Object.freeze({
    paymentId: input.paymentId,
    institutionId: input.institutionId,
    cycleId: input.cycleId,
    studentId: input.studentId,
    amount: input.amount,
    method: input.method,
    reference,
    allocations: Object.freeze(
      // Orden estable: el recibo se ve igual generado offline y regenerado por la API.
      [...allocations].sort((a, b) => a.chargeId.localeCompare(b.chargeId)),
    ),
    creditAmount,
    effectiveAt: input.effectiveAt,
    capturedAt: input.capturedAt,
    operationalDate: input.operationalDate,
    userId: input.userId,
    installationId: input.installationId,
    note: input.note?.trim() ? input.note.trim().slice(0, 500) : null,
    idempotencyKey: idempotencyKey(
      input.institutionId,
      "PAGO",
      input.paymentId,
    ),
    gradeName: input.gradeName,
    sectionName: input.sectionName,
    studentBalanceBefore,
    studentBalanceAfter: subtract(studentBalanceBefore, allocated),
  });
}

/**
 * Distribuye automaticamente un importe entre los cargos pendientes, del mas
 * antiguo al mas reciente. Es una ayuda de captura: el usuario puede ajustarla
 * antes de confirmar, y `buildPayment` vuelve a validar el resultado.
 */
export function autoAllocate(
  amount: Money,
  charges: readonly Charge[],
  asOf?: OperationalDate,
): AllocationInput[] {
  const ordered = [...charges]
    .filter((charge) => isPositive(chargeOutstanding(charge, asOf)))
    .sort((a, b) => {
      const dateA = a.dueDate ?? "9999-12-31";
      const dateB = b.dueDate ?? "9999-12-31";
      if (dateA !== dateB) return dateA < dateB ? -1 : 1;
      return a.id.localeCompare(b.id);
    });

  let remaining = amount;
  const result: AllocationInput[] = [];
  for (const charge of ordered) {
    if (!isPositive(remaining)) break;
    const outstanding = chargeOutstanding(charge, asOf);
    const applied =
      compare(remaining, outstanding) <= 0 ? remaining : outstanding;
    result.push({ chargeId: charge.id, amount: applied });
    remaining = subtract(remaining, applied);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Anulaciones
// ---------------------------------------------------------------------------

export interface VoidPaymentInput {
  readonly voidId: Ulid;
  readonly institutionId: Ulid;
  readonly payment: {
    readonly id: Ulid;
    readonly status: PaymentStatus;
    readonly amount: Money;
    readonly studentId: Ulid;
    readonly cycleId: Ulid;
    readonly allocations: readonly { chargeId: Ulid; amount: Money }[];
  };
  readonly reason: string;
  readonly userId: Ulid;
  readonly installationId: Ulid;
  readonly at: Instant;
  readonly operationalDate: OperationalDate;
}

export interface Compensation {
  readonly compensationId: Ulid;
  readonly institutionId: Ulid;
  readonly paymentId: Ulid;
  readonly studentId: Ulid;
  readonly cycleId: Ulid;
  /** Importe negativo: revierte exactamente lo aplicado. */
  readonly amount: Money;
  readonly allocations: readonly { chargeId: Ulid; amount: Money }[];
  readonly reason: string;
  readonly userId: Ulid;
  readonly installationId: Ulid;
  readonly at: Instant;
  readonly operationalDate: OperationalDate;
  readonly idempotencyKey: string;
}

/**
 * Una anulacion NO borra el pago: genera una operacion compensatoria con importes
 * negativos que revierte cada aplicacion. El pago original queda inmutable y su
 * estado pasa a ANULADO. Los saldos se reconstruyen sumando ambas operaciones.
 */
export function buildCompensation(input: VoidPaymentInput): Compensation {
  const { payment } = input;
  assertDomain(
    payment.status !== "ANULADO",
    "PAGO_YA_ANULADO",
    "El pago ya fue anulado previamente",
    { paymentId: payment.id },
  );
  assertDomain(
    payment.status === "CONFIRMADO",
    "PAGO_NO_CONFIRMADO",
    "Solo se pueden anular pagos confirmados por el servidor",
    { paymentId: payment.id, status: payment.status },
  );
  const reason = input.reason.trim();
  assertDomain(
    reason.length >= 5,
    "MONTO_INVALIDO",
    "Debe indicar el motivo de la anulación",
    {},
  );

  return Object.freeze({
    compensationId: input.voidId,
    institutionId: input.institutionId,
    paymentId: payment.id,
    studentId: payment.studentId,
    cycleId: payment.cycleId,
    amount: money(-payment.amount.minor, payment.amount.currency),
    allocations: Object.freeze(
      payment.allocations.map((allocation) => ({
        chargeId: allocation.chargeId,
        amount: money(-allocation.amount.minor, allocation.amount.currency),
      })),
    ),
    reason: reason.slice(0, 500),
    userId: input.userId,
    installationId: input.installationId,
    at: input.at,
    operationalDate: input.operationalDate,
    idempotencyKey: idempotencyKey(
      input.institutionId,
      "ANULACION",
      input.voidId,
    ),
  });
}

/** Verificacion de reconciliacion: un pago y su compensacion deben sumar exactamente cero. */
export function isFullyCompensated(
  payment: BuiltPayment,
  compensation: Compensation,
): boolean {
  return equals(
    add(payment.amount, compensation.amount),
    zero(payment.amount.currency),
  );
}
